import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

// Backdrop overlay
export const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease-out;

  &:focus {
    outline: none;
  }
`;

// Main modal container
export const ModalContainer = styled.div.attrs({
  role: 'dialog',
  'aria-modal': 'true',
  'aria-labelledby': 'modal-title',
  'aria-describedby': 'modal-description',
})`
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  animation: ${slideDown} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  @media (max-width: 768px) {
    max-width: 90vw;
    margin: 0 auto;
  }
`;

// Content area with white background
export const ModalContent = styled.div`
  background: #eceaf8;
  margin: 2px;
  padding: 40px 32px 32px;
  text-align: center;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border-radius: 20px;

  @media (max-width: 480px) {
    padding: 32px 24px 24px;
    min-height: 350px;
  }
`;

// Title
export const ModalTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 32px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 22px;
    margin-bottom: 24px;
  }
`;

// Description text
export const ModalDescription = styled.p`
  font-size: 16px;
  color: #4b5563;
  margin: 0 0 40px 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 32px;
  }
`;

// Phone number highlight
export const PhoneNumber = styled.span`
  font-weight: 600;
  color: #1f2937;
`;

// Code input container
export const CodeInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

// Individual code input field
export const CodeInput = styled.input.attrs({
  type: 'text',
  inputMode: 'numeric',
  pattern: '[0-9]*',
  maxLength: 1,
  'aria-label': (props) => `Digit ${props.index + 1} of verification code`,
})`
  width: 56px;
  height: 56px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    transform: scale(1.02);
  }

  &:hover:not(:focus) {
    border-color: #9ca3af;
  }

  &.error {
    border-color: #ef4444;
    background: #fef2f2;
    animation: ${shake} 0.5s ease-in-out;
  }

  &.filled {
    border-color: #10b981;
    background: #f0fdf4;
  }

  /* Remove spinner arrows for number inputs */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
`;

// Error message
export const ErrorMessage = styled.div.attrs({
  role: 'alert',
  'aria-live': 'polite',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
  min-height: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;

  &:empty {
    margin-bottom: 0;
  }
`;

// Error icon
export const ErrorIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #dc2626;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;

  &::before {
    content: '!';
  }
`;

// Resend code button
export const ResendButton = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  margin: 0 0 32px 0;
  max-width: fit-content;
  margin-inline: auto;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;

  &:hover:not(:disabled) {
    color: #2563eb;
    background: #f0f9ff;
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 24px;
  }
`;

// Button container
export const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: auto;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

// Base button styles
const BaseButton = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 14px 20px;
    font-size: 15px;
  }
`;

// Cancel button
export const CancelButton = styled(BaseButton)`
  background: #f9fafb;
  border: 1px solid #d1d5db;
  color: #374151;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
    transform: translateY(-1px);
  }
`;

// Verify button
export const VerifyButton = styled(BaseButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    box-shadow: none;
  }
`;

// Loading spinner for verify button
export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Screen reader only text (for accessibility)
export const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
